import { useMemo, useState } from "react";
import { Link } from "wouter";
import { Shell } from "@/components/layout/Shell";
import { useSeo } from "@/hooks/use-seo";
import { ShareButtons } from "@/components/social/ShareButtons";
import { ColorTrait } from "@/data/colors";
import { getMatrixPalette, analyzeMatrix, MatrixCategory, MatrixAnalysis } from "@/lib/decision-matrix";

const CATEGORIES: { key: MatrixCategory; label: string; helper: string }[] = [
  { key: "fears", label: "Fears", helper: "What color feels like what could go wrong?" },
  { key: "aspirations", label: "Aspirations", helper: "What color feels like the outcome you actually want?" },
  { key: "hesitations", label: "Hesitations", helper: "What color feels like what's making you pause?" },
];

const PALETTE = getMatrixPalette();

export default function DecisionMatrix() {
  useSeo({
    title: "Color Decision Matrix | Mychromamind",
    description:
      "Type in a real dilemma, then pick colors for your Fears, Aspirations, and Hesitations — get an instant, color-psychology reading of what's actually driving your choice.",
    path: "/decision-matrix",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: "Color Decision Matrix",
      applicationCategory: "LifestyleApplication",
      operatingSystem: "Any (Web)",
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
      description:
        "Pick colors for your Fears, Aspirations, and Hesitations about a real decision and get an instant color-psychology reading of what's driving the choice.",
      url: "https://mychromamind.com/decision-matrix",
    },
  });

  const [dilemma, setDilemma] = useState("");
  const [picks, setPicks] = useState<Partial<Record<MatrixCategory, ColorTrait>>>({});
  const [result, setResult] = useState<MatrixAnalysis | null>(null);

  const complete = picks.fears && picks.aspirations && picks.hesitations;

  const handlePick = (category: MatrixCategory, color: ColorTrait) => {
    setPicks((p) => ({ ...p, [category]: color }));
  };

  const handleReveal = () => {
    if (!picks.fears || !picks.aspirations || !picks.hesitations) return;
    setResult(
      analyzeMatrix({ fears: picks.fears, aspirations: picks.aspirations, hesitations: picks.hesitations }, dilemma)
    );
  };

  const handleReset = () => {
    setDilemma("");
    setPicks({});
    setResult(null);
  };

  if (result) {
    return (
      <Shell>
        <div className="flex-1 px-5 sm:px-6 py-10 sm:py-16 max-w-3xl mx-auto w-full">
          <h1 className="text-3xl sm:text-4xl font-serif text-center mb-3">Your Decision Matrix</h1>
          {dilemma.trim() && (
            <p className="text-center text-muted-foreground text-sm mb-10 italic">"{dilemma.trim()}"</p>
          )}

          <div className="space-y-6">
            {result.insights.map((insight) => (
              <div
                key={insight.category}
                className="flex flex-col sm:flex-row gap-4 sm:gap-6 items-start bg-card p-5 sm:p-6 rounded-2xl border border-border"
              >
                <div
                  className="w-14 h-14 rounded-full flex-shrink-0 ring-4 ring-border"
                  style={{ backgroundColor: insight.color.hex }}
                  aria-hidden="true"
                />
                <div className="text-left">
                  <h2 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                    {insight.label} — {insight.color.name}
                  </h2>
                  <p className="text-foreground leading-relaxed">{insight.text}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 bg-muted/60 p-5 sm:p-6 rounded-2xl border border-border/60">
            <h2 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
              The Bigger Picture
            </h2>
            <p className="text-foreground leading-relaxed">{result.synthesis}</p>
          </div>

          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center items-center">
            <button
              onClick={handleReset}
              className="px-6 py-3 rounded-full border border-border hover:bg-secondary/5 transition-colors font-medium text-sm"
            >
              Try Another Dilemma
            </button>
            <Link
              href="/quiz/decision"
              className="px-6 py-3 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors font-medium text-sm"
            >
              Take the Decision Quiz
            </Link>
          </div>

          <ShareButtons
            label="Share this reading"
            text="I just mapped my Fears, Aspirations, and Hesitations with the Mychromamind Color Decision Matrix."
            className="mt-10"
          />

          <p className="mt-8 text-xs text-muted-foreground max-w-md mx-auto text-center">
            For fun and self-reflection only — not a substitute for professional advice.
          </p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="flex-1 px-5 sm:px-6 py-10 sm:py-16 max-w-3xl mx-auto w-full">
        <div className="text-center mb-8 sm:mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-muted text-foreground text-xs font-medium tracking-wide uppercase mb-4">
            <span className="w-2 h-2 rounded-full bg-primary" />
            Color Decision Matrix
          </div>
          <h1 className="text-3xl sm:text-5xl font-serif text-foreground leading-[1.1] mb-3">
            What's really driving this choice?
          </h1>
          <p className="text-sm sm:text-lg text-muted-foreground max-w-xl mx-auto leading-relaxed">
            Name the dilemma, then pick a color for each block below. We'll translate your palette into a
            reading of what's underneath the decision.
          </p>
        </div>

        <label htmlFor="dilemma" className="block text-sm font-medium text-foreground mb-2">
          What decision are you weighing? <span className="text-muted-foreground font-normal">(optional)</span>
        </label>
        <textarea
          id="dilemma"
          value={dilemma}
          onChange={(e) => setDilemma(e.target.value)}
          placeholder="e.g. Whether to take the new job offer, or stay where I am..."
          rows={2}
          maxLength={200}
          className="w-full rounded-xl border border-border bg-card p-4 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none mb-10"
        />

        <div className="space-y-10">
          {CATEGORIES.map(({ key, label, helper }) => (
            <div key={key}>
              <h2 className="text-lg font-serif mb-1">{label}</h2>
              <p className="text-xs text-muted-foreground mb-4">{helper}</p>
              <div
                role="group"
                aria-label={`Pick a color for ${label}`}
                className="grid grid-cols-5 sm:grid-cols-10 gap-3"
              >
                {PALETTE.map((color) => {
                  const selected = picks[key]?.id === color.id;
                  return (
                    <button
                      key={color.id}
                      type="button"
                      onClick={() => handlePick(key, color)}
                      aria-pressed={selected}
                      aria-label={color.name}
                      title={color.name}
                      className="flex items-center justify-center focus:outline-none"
                    >
                      <span
                        className={`w-8 h-8 sm:w-9 sm:h-9 rounded-full ring-2 transition-all ${
                          selected ? "ring-primary scale-110" : "ring-border hover:ring-primary/50 hover:scale-105"
                        }`}
                        style={{ backgroundColor: color.hex }}
                      />
                    </button>
                  );
                })}
              </div>
              {picks[key] && (
                <p className="text-xs text-muted-foreground mt-2">
                  Selected: <span className="text-foreground font-medium">{picks[key]!.name}</span>
                </p>
              )}
            </div>
          ))}
        </div>

        <div className="mt-10 flex justify-center">
          <button
            onClick={handleReveal}
            disabled={!complete}
            className="px-8 py-3.5 rounded-full bg-primary text-primary-foreground font-medium text-sm hover:bg-primary/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Reveal My Analysis
          </button>
        </div>

        <p className="text-xs text-muted-foreground text-center mt-8 max-w-md mx-auto">
          For entertainment and self-reflection only — not psychological, medical, legal, or financial
          advice.
        </p>
      </div>
    </Shell>
  );
}
